/**
 * AttorneyBrianConnect — Settings panel where an owner/manager connects their
 * OWN Claude Code to this company's Attorney Brian (MCP server).
 *
 * Flow (docs/attorney-brian-for-members.md §2C): mint a scoped revocable
 * token, show the one command to paste into Claude Code, list + revoke
 * existing connections. The token is displayed ONCE.
 */
import { Bot, Copy, Check, ShieldAlert, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { request } from "../lib/api";

interface McpTokenRow {
  id: string;
  label: string;
  user_name: string;
  user_email: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

const MCP_URL = `${typeof window !== "undefined" ? window.location.origin : ""}/api/mcp`;

function connectCommand(token: string): string {
  return `claude mcp add --transport http attorney-brian ${MCP_URL} --header "Authorization: Bearer ${token}"`;
}

export function AttorneyBrianConnect({ canManage }: { canManage: boolean }) {
  const [tokens, setTokens] = useState<McpTokenRow[]>([]);
  const [minted, setMinted] = useState<{ token: string; expires_at: string } | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await request<{ ok: true; tokens: McpTokenRow[] }>("/api/mcp/tokens");
      setTokens(r.tokens);
    } catch (e: any) {
      setError(e?.message || "Could not load connections");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function mint() {
    setBusy(true); setError(null);
    try {
      const r = await request<{ ok: true; token: string; expires_at: string }>("/api/mcp/tokens", {
        method: "POST",
        body: { label: label.trim() || "Claude Code" },
      });
      setMinted({ token: r.token, expires_at: r.expires_at });
      setLabel("");
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not create the connection");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!window.confirm("Revoke this connection? That Claude Code will lose access immediately.")) return;
    try {
      await request<{ ok: true }>(`/api/mcp/tokens/${id}`, { method: "DELETE" });
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not revoke");
    }
  }

  async function copyCommand() {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(connectCommand(minted.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard may be blocked; the text stays selectable */ }
  }

  const active = tokens.filter((t) => !t.revoked_at);

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header">
        <h2 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Bot size={17} aria-hidden="true" /> Attorney Brian — connect your Claude
        </h2>
      </div>
      <div className="stacked-form">
        <p className="muted small" style={{ margin: 0 }}>
          Attorney Brian runs inside <strong>your own Claude</strong> — it connects into your
          company here, already trained on the funding workflow: analysis, lender matching, packets, BRM hand-offs, and the legal
          guardrails. Your data stays scoped to your company, and we never see your Claude login.
        </p>

        <div style={{ border: "1px solid var(--ne-silver-1)", padding: 14, display: "grid", gap: 8 }}>
          <strong>Easiest: add Brian to the Claude app (no terminal)</strong>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }}>
            <li>In Claude (desktop or claude.ai) open <strong>Settings → Connectors → Add custom connector</strong>.</li>
            <li>Paste this address:&nbsp;
              <code style={{ fontSize: 12, padding: "2px 6px", background: "var(--ne-black-2, #111)", color: "var(--ne-silver-3, #ddd)" }}>{MCP_URL}</code>
              <button className="btn-secondary" style={{ marginLeft: 8, padding: "2px 8px" }} onClick={() => navigator.clipboard?.writeText(MCP_URL)}>Copy</button>
            </li>
            <li>Click <strong>Connect</strong> → sign in with your staff email &amp; password → <strong>Allow</strong>.</li>
          </ol>
          <span className="muted small">Open a chat and say “use Attorney Brian and list my clients.” The connection shows below and can be revoked here anytime. It does not expire.</span>
        </div>
        <div className="banner-warning banner-rail" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <ShieldAlert size={16} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>Runs on your own Claude subscription</strong> (Claude Pro or Max — the regular Claude app is enough, no terminal needed). It is not included in your software plan.
            Brian can run lender recommendations, request missing details, build packets, and record approvals; anything that emails a client or a lender only happens after
            you confirm. Soft pulls, billing, and staff changes stay in the app — Brian
            never touches them.
          </span>
        </div>

        {error && <div className="banner-warning">{error}</div>}

        {canManage && !minted && (
          <details open>
            <summary className="muted small" style={{ cursor: "pointer" }}>Create a connection token</summary>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Name this connection (e.g. Office MacBook)"
              style={{ flex: 1, minWidth: 220 }}
            />
            <button className="btn-primary" onClick={mint} disabled={busy}>
              {busy ? "Creating…" : "Create a token"}
            </button>
          </div>
          </details>
        )}

        {minted && (
          <div style={{ border: "1px solid var(--ne-silver-1)", padding: 14, display: "grid", gap: 10 }}>
            <strong>Paste this one command into your terminal:</strong>
            <code style={{ display: "block", whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 12, padding: 10, background: "var(--ne-black-2, #111)", color: "var(--ne-silver-3, #ddd)" }}>
              {connectCommand(minted.token)}
            </code>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={copyCommand} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                {copied ? "Copied" : "Copy command"}
              </button>
              <span className="muted small">
                Shown once — copy it now. Then open Claude Code and say “use Attorney Brian.”
                Expires {new Date(minted.expires_at).toLocaleDateString()}.
              </span>
            </div>
            <button className="btn-secondary" style={{ justifySelf: "start" }} onClick={() => setMinted(null)}>Done</button>
          </div>
        )}

        {active.length > 0 && (
          <div style={{ display: "grid", gap: 6 }}>
            <strong className="small">Active connections</strong>
            {active.map((t) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ flex: 1 }}>
                  {t.label} <span className="muted">· {t.user_name} · {t.last_used_at ? `last used ${new Date(t.last_used_at).toLocaleDateString()}` : "never used"} · expires {new Date(t.expires_at).toLocaleDateString()}</span>
                </span>
                {canManage && (
                  <button className="btn-secondary" onClick={() => revoke(t.id)} aria-label={`Revoke ${t.label}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Trash2 size={13} aria-hidden="true" /> Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
